import { FC } from 'react';
import { IoLocationSharp } from 'react-icons/io5';
import { StyledLink } from 'components/modules/Header/Header.styles';
import { useDefaultColorValue } from 'services/queries/app';
import { getNavLinks } from '../links';
import { ILinkProps, ISubmenuProps } from '../Navbar.interface';
import { useAppContext } from '../../../../provider/AppProvider/AppProvider';
import MenuItem from '../MenuItem';
import { IMenuListProps } from './MenuList.interface';

const MenuList: FC<IMenuListProps> = ({ tagline, handleCloseNavbar }) => {
  const navLinks = getNavLinks();

  const colors = useDefaultColorValue();
  const {
    setup: {
      header: { menu },
      menu_categories = [],
    },
    about: { platform_version },
    config: { defaultLabels },
    baseURL,
  } = useAppContext();

  let enabled_modules = (menu?.enabled_modules ?? []) as string[];
  if (parseFloat(platform_version) < 10.0) {
    enabled_modules = enabled_modules.filter((title) => title !== 'categories');
  }

  const filteredNavLinks = navLinks.filter((link) => {
    if (['about', 'home'].includes(link.title)) return true;
    return enabled_modules.includes(link.title);
  });

  const getNavLabel = (title: string, fallback: string): string => {
    const overridden = menu?.label_overrides?.[title];
    return overridden && overridden.trim() !== ''
      ? overridden
      : defaultLabels[title as keyof typeof defaultLabels] || fallback;
  };

  const processedNavLinks: ILinkProps[] = filteredNavLinks.map(
    (navItem: ILinkProps) => {
      const updatedLabel = getNavLabel(navItem.title, navItem.label);

      if (navItem.title === 'categories') {
        const resolvedUrl = enabled_modules.includes('categories')
          ? '/category'
          : navItem.url;

        const submenu: ISubmenuProps[] = menu_categories
          .filter((category) => category.toggle)
          .map((category) => ({
            label: getNavLabel(category.label, category.label),
            url: `/category/${category.value}`,
          }));

        return {
          ...navItem,
          label: updatedLabel,
          url: resolvedUrl,
          submenu,
        };
      }

      const updatedSubmenu: ISubmenuProps[] | undefined = navItem.submenu
        ?.filter((subItem) => {
          const subItemTitle = subItem.title ?? '';
          return enabled_modules.includes(subItemTitle);
        })
        .map((subItem) => ({
          ...subItem,
          label: getNavLabel(subItem.title ?? '', subItem.label),
        }));

      return {
        ...navItem,
        label: updatedLabel,
        submenu: updatedSubmenu,
      };
    },
  );

  const renderMenuItems = (links: ILinkProps[]) => {
    return links.map((link: ILinkProps) => (
      <MenuItem
        key={link.title}
        label={link.label}
        url={link.url}
        submenu={link.submenu ?? []}
        handleCloseNavbar={handleCloseNavbar}
      />
    ));
  };

  return (
    <ul className="flex lg:flex-row lg:flex-wrap lg:justify-between lg:items-center w-full mb-0 flex-col items-start">
      {renderMenuItems(processedNavLinks)}

      <li className="nav-item lg:hidden w-full">
        <h1 className="text-[#666666] text-sm w-full py-2 px-5">{tagline}</h1>
      </li>
      <li className="nav-item lg:hidden w-full">
        <span className="flex-row items-center justify-start p-2 pl-4 flex bg-[#f5f0eb]">
          <IoLocationSharp color={colors.medium} size={22} />
          <StyledLink
            to={`${String(baseURL)}/unlock-code`}
            color={colors.dark}
            style={{ color: colors.dark }}
          >
            Unlock code
          </StyledLink>
        </span>
      </li>
    </ul>
  );
};

export default MenuList;
